import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mail, LogOut } from 'lucide-react';

interface AvatarButtonMenuProps {
  className?: string;
}

export function AvatarButtonMenu({ className }: AvatarButtonMenuProps) {
  const { user, isAuthenticated, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = useCallback(() => setMenuOpen((prev) => !prev), []);

  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (event: Event) => {
      const target = event.target as Node;
      if (
        (buttonRef.current && buttonRef.current.contains(target)) ||
        (menuRef.current && menuRef.current.contains(target))
      ) {
        return;
      }
      setMenuOpen(false);
    };

    const handleKeyDown = (event: Event) => {
      if (event instanceof KeyboardEvent && (event.key === 'Escape' || event.key === 'Esc')) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  if (!isAuthenticated || !user) {
    return null;
  }

  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const avatarBg = stringToColor(initials);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        className={`flex-shrink-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors ${className || ''}`}
        aria-label="User menu"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.name}
            className="w-full h-full rounded-full object-cover"
          />
        ) : (
          <div
            className="w-full h-full rounded-full flex items-center justify-center text-white font-medium text-sm"
            style={{ backgroundColor: avatarBg }}
          >
            {initials}
          </div>
        )}
      </button>

      {menuOpen && (
        <div
          ref={menuRef}
          className="absolute right-0 top-12 z-50 w-72 rounded-2xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
          role="menu"
        >
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm"
                style={{ backgroundColor: avatarBg }}
              >
                {initials}
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {user.name}
                </span>
                <div className="flex items-center gap-1">
                  <Mail className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {user.email}
                  </span>
                </div>
              </div>
            </div>
            <hr className="my-3 border-gray-200 dark:border-gray-700" />
            <button
              onClick={() => {
                setMenuOpen(false);
                logout();
              }}
              className="w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-950/30"
              role="menuitem"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    '#4285F4',
    '#EA4335',
    '#FBBC04',
    '#34A853',
    '#FF6D01',
    '#46BDC6',
    '#7B1FA2',
    '#E91E63',
    '#00BCD4',
    '#8BC34A',
  ];
  return colors[Math.abs(hash) % colors.length];
}
